import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import { FaCloud, FaTable } from "react-icons/fa";
import Utils from "../../utils";
import { azureCostActions } from "../../actions/azureCost.actions";
import { Alert, Button, Modal } from "react-bootstrap";
import GridHelper from "../helpers/gridHelper";
import AzureCostCharts from "./AzureCostCharts";

function AzureCostGrid({ hideTitle }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const azureCost = useSelector((state) => state.azureCost);

  const [viewMode, setViewMode] = useState("daily"); // daily, monthly, quarterly
  const [showDataModal, setShowDataModal] = useState(false);

  useEffect(() => {
    // Fetch chart data on initial load (with larger page sizes)
    dispatch(azureCostActions.getChartData({}));
  }, [dispatch]);

  // Default the table to newest-first. The API returns oldest-first when no
  // sort is supplied, which surfaced the earliest data (1/1/2025) at the top.
  // Only applied when the user hasn't chosen a sort via the column headers.
  const defaultOrderColumn = viewMode === "daily" ? "CostDate" : "Year";
  const withDefaultSort = (queryParams) => {
    if (!queryParams.order_by) {
      queryParams.order_by = defaultOrderColumn;
      queryParams.order_direction = "Desc";
    }
    return queryParams;
  };

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);

    if (!queryParams.pageNumber) {
      queryParams.pageNumber = 1;
    }

    // Fetch data based on view mode (newest-first by default)
    queryParams = withDefaultSort(queryParams);
    if (viewMode === "daily") {
      dispatch(azureCostActions.getDaily(queryParams));
    } else if (viewMode === "monthly") {
      dispatch(azureCostActions.getMonthly(queryParams));
    } else if (viewMode === "quarterly") {
      dispatch(azureCostActions.getQuarterly(queryParams));
    }
    // eslint-disable-next-line
  }, [location, viewMode]);

  // Amortized column first (it's the figure we manage against), actual kept
  // beside it so the two bases can be compared without leaving the page.
  const getDisplayColumns = () => {
    if (viewMode === "daily") {
      return ["subscriptionName", "costDate", "amortizedCost", "cost", "dayOfWeek", "year", "month", "quarter"];
    } else if (viewMode === "monthly") {
      return ["subscriptionName", "year", "month", "monthlyAmortizedCost", "monthlyCost", "daysWithData"];
    } else if (viewMode === "quarterly") {
      return ["subscriptionName", "year", "quarter", "quarterlyAmortizedCost", "quarterlyCost", "daysWithData"];
    }
    return ["subscriptionName", "costDate", "amortizedCost", "cost"];
  };

  const getData = () => {
    let rawData = [];
    if (viewMode === "daily") {
      rawData = azureCost.dailyResult?.data || [];
    } else if (viewMode === "monthly") {
      rawData = azureCost.monthlyResult?.data || [];
    } else if (viewMode === "quarterly") {
      rawData = azureCost.quarterlyResult?.data || [];
    }

    // Amortized values are nullable (rows imported before the field existed), so
    // they render as an em dash rather than $0.00, which would read as no spend.
    const money = (value) =>
      value !== undefined && value !== null ? `$${value.toFixed(2)}` : "";
    const nullableMoney = (value) =>
      value !== undefined && value !== null ? `$${value.toFixed(2)}` : "\u2014";

    return rawData.map((item) => ({
      ...item,
      costDate: item.costDate ? Utils.convertToDateOnly(item.costDate) : "",
      cost: money(item.cost),
      monthlyCost: money(item.monthlyCost),
      quarterlyCost: money(item.quarterlyCost),
      amortizedCost: nullableMoney(item.amortizedCost),
      monthlyAmortizedCost: nullableMoney(item.monthlyAmortizedCost),
      quarterlyAmortizedCost: nullableMoney(item.quarterlyAmortizedCost),
      // Raw numeric kept for the edit modal, which needs a value not a label.
      amortizedCostValue: item.amortizedCost ?? null,
    }));
  };

  const getPaginationData = () => {
    if (viewMode === "daily") {
      return azureCost.dailyResult;
    } else if (viewMode === "monthly") {
      return azureCost.monthlyResult;
    } else if (viewMode === "quarterly") {
      return azureCost.quarterlyResult;
    }
    return null;
  };

  const isLoading = () => {
    return azureCost.loadingDaily || azureCost.loadingMonthly || azureCost.loadingQuarterly;
  };

  const paginationData = getPaginationData();
  const data = getData();

  // Seed the grid's sort indicator from the URL, falling back to the
  // newest-first default so the header arrow matches the data ordering.
  const urlParams = Utils.getQueryStringParams(location);
  const activeOrderBy = urlParams.order_by || defaultOrderColumn;
  const activeOrderDirection =
    (urlParams.order_direction || "Desc") === "Asc" ? 1 : -1;

  // Get chart data from dedicated chart data fetch (larger datasets)
  const chartData = azureCost.chartData || {};
  const chartLoading = azureCost.loadingChartData;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        {!hideTitle && <Title text="Azure Costs" icon={<FaCloud className="mb-1" />} />}
        <Button
          variant="outline-secondary"
          size="sm"
          className="ms-auto"
          onClick={() => setShowDataModal(true)}
        >
          <FaTable className="me-1 mb-1" /> View Data
        </Button>
      </div>

      {azureCost.error && (
        <Alert variant="danger" dismissible>
          <strong>Error:</strong> Failed to load Azure costs.
        </Alert>
      )}

      {/* Charts Section */}
      <AzureCostCharts
        dailyData={chartData.daily || []}
        monthlyData={chartData.monthly || []}
        quarterlyData={chartData.quarterly || []}
        loading={chartLoading}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Raw cost records — tucked behind "View Data" instead of always taking up page
          space, so the chart above gets to be the main event. */}
      <Modal show={showDataModal} onHide={() => setShowDataModal(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Azure Cost Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EnhancedGrid
            gridId="azurecost"
            data={data}
            displayColumns={getDisplayColumns()}
            emptyState={{
              icon: <FaCloud size={80} className="text-muted mb-3" />,
              message: "No cost data found",
              subtitle: "Cost data will appear here once it's available for this period.",
            }}
            idColumn={"id"}
            pageNumber={(paginationData?.pageNumber ?? 1) - 1}
            totalPages={paginationData?.totalPages ?? 0}
            loading={isLoading()}
            onPageChange={GridHelper.handlePageClick}
            onSort={GridHelper.handleSortClick}
            initialOrderBy={activeOrderBy}
            initialOrderDirection={activeOrderDirection}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AzureCostGrid;
